import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Button, Modal, Panel, Spinner } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { TelehealthNowPickerModal } from '../telehealth/TelehealthNowPickerModal';
import { AfterHoursAvailability } from './AfterHoursAvailability';
import { AvailabilityCalendar } from './AvailablilityCalendar';
import { NonSpecificAvailability } from './NonSpecificAvailability';
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from 'src/hooks/useAsyncSelector';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import {
    AutomedClinicAvailabilityTimeModel,
    AutomedDoctorAvailabilityTimeModel,
    availabilityService,
} from 'src/services/availability';
import { StateEnum } from 'src/services/telehealth';
import { useDispatch, useSelector } from 'src/store';
import { changeTime, setAppointmentTypeId } from 'src/store/appointment';
import {
    filterToAfterHoursAppointmentTypes,
    isAfterHours,
    isExpressTelehealth,
    isTelehealthNow,
} from 'src/utils/appointmentTypes';
import { toAutomedDoctorAvailabilityTimeModel } from 'src/utils/mapAutomedTimeTypes';
import { doctorName } from 'src/utils/name';

type Props = {
    isOpen: boolean;
    setOpen(isOpen: boolean): void;
    onChange?(timeslot: Moment): void;
};

export function AvailabilityPopup({ isOpen, setOpen, onChange }: Props) {
    const dispatch = useDispatch();
    const breakpoint = useBreakpoints();

    const [selectedAfterHoursAppointmentTypeId, setSelectedAfterHoursAppointmentTypeId] = useState<string>();
    const { dateTime, timeSlot, clinicId, doctorErpId, appointmentTypeId } = useSelector((state) => state.appointment);
    const [selected, setSelected] = useState<{
        dateTime: Moment;
        timeSlot: AutomedDoctorAvailabilityTimeModel | AutomedClinicAvailabilityTimeModel;
    }>({
        dateTime: moment(dateTime),
        timeSlot,
    });
    const { data: appointmentType } = useAppointmentTypeSelector();
    const { data: doctor } = useDoctorSelector();
    const { data: clinic } = useClinicSelector();

    const {
        data: afterHoursAppointmentTypes,
        isLoading: isLoadingAppointmentTypes,
        invoke: getAppointmentTypes,
    } = useAsyncData(
        {
            method: availabilityService.GetAppointmentTypes,
            service: availabilityService,
            selector: filterToAfterHoursAppointmentTypes,
            invokeOnMount: false,
        },
        clinicId
    );

    const {
        data: firstAvailable,
        isLoading: _isLoadingFirstAvailable,
        invoke: getFirstAvailable,
    } = useAsyncData(
        {
            method: availabilityService.GetFirstAvailable,
            service: availabilityService,
            invokeOnMount: false,
        },
        clinicId,
        appointmentTypeId?.toString(),
        doctorErpId
    );

    // whenever redux changes, update local selected state
    useEffect(() => {
        setSelected({ dateTime: moment(dateTime), timeSlot });
    }, [dateTime, timeSlot]);

    useEffect(() => {
        if (isOpen && appointmentType) {
            if (!isAfterHours(appointmentType)) {
                getFirstAvailable();
            } else if (!afterHoursAppointmentTypes) {
                getAppointmentTypes();
            }
        }
    }, [afterHoursAppointmentTypes, appointmentType, getAppointmentTypes, getFirstAvailable, isOpen]);

    const preselectedAppointmentType = useMemo(
        () => afterHoursAppointmentTypes?.find((x) => x.appointmentTypeId === appointmentTypeId),
        [afterHoursAppointmentTypes, appointmentTypeId]
    );

    if (appointmentType && isTelehealthNow(appointmentType)) {
        return <TelehealthNowPickerModal state={clinic?.state as StateEnum} isOpen={isOpen} setOpen={setOpen} />;
    }

    const content = (
        <>
            <div className="px-4 pb-4">
                <p className="font-base mt-1 text-sm text-gray-600">
                    Change your session with {doctorName(doctor)}, currently scheduled for{' '}
                    <strong className="font-semibold">
                        {moment(dateTime).format('ddd, D MMM')} at {moment(dateTime).format('LT')}
                    </strong>
                    .
                </p>
            </div>
            {appointmentType &&
                (!isAfterHours(appointmentType) ? (
                    isExpressTelehealth(appointmentType) ? (
                        <NonSpecificAvailability
                            appointmentType={appointmentType}
                            clinicId={clinicId}
                            doctorId={doctorErpId}
                            selected={selected?.dateTime}
                            onSelectTimeSlot={({ date, timeSlot }) => {
                                setSelected({ dateTime: moment(date), timeSlot });
                            }}
                            firstAvailableDate={firstAvailable?.[0]?.firstAvailable?.date}
                            futureApptWeeks={clinic?.futureApptWeeks}
                            infoText={
                                <>
                                    <strong className="font-semibold">Please note:</strong> This is a request for a
                                    same-day telehealth call. A doctor will contact you once they become available.
                                </>
                            }
                        />
                    ) : (
                        <AvailabilityCalendar
                            appointmentType={appointmentType}
                            clinicId={clinicId}
                            doctorId={doctorErpId}
                            selected={selected?.dateTime}
                            onSelectTimeSlot={({ date, timeSlot }) => {
                                setSelected({ dateTime: moment(date), timeSlot });
                            }}
                            firstAvailableDate={firstAvailable?.[0]?.firstAvailable?.date}
                            futureApptWeeks={clinic?.futureApptWeeks}
                        />
                    )
                ) : isLoadingAppointmentTypes ? (
                    <Spinner />
                ) : (
                    <AfterHoursAvailability
                        appointmentTypes={afterHoursAppointmentTypes}
                        preselectedAppointmentType={preselectedAppointmentType}
                        onSelectTimeSlot={({ date, appointmentTypeId, timeSlot }) => {
                            setSelected({ dateTime: moment(date), timeSlot });
                            setSelectedAfterHoursAppointmentTypeId(appointmentTypeId);
                        }}
                        className="px-4 pb-4"
                        siteId={clinicId}
                        selected={selected?.dateTime}
                    />
                ))}
            <div
                className={classNames(
                    'flex flex-col-reverse flex-wrap justify-end gap-4 p-4 lg:flex-row',
                    'sticky bottom-0 -mt-px border-t bg-white lg:static lg:m-0 lg:border-none lg:bg-transparent'
                )}
            >
                <Button color="gray" variant="link" onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button
                    disabled={!selected}
                    onClick={() => {
                        setOpen(false);
                        dispatch(
                            changeTime({
                                date: selected.dateTime.toDate(),
                                timeSlot: toAutomedDoctorAvailabilityTimeModel(selected.timeSlot),
                            })
                        );
                        if (selectedAfterHoursAppointmentTypeId) {
                            dispatch(setAppointmentTypeId(selectedAfterHoursAppointmentTypeId));
                        }
                        onChange?.(selected!.dateTime);
                        toast.success('Your appointment date/time has been updated!');
                    }}
                >
                    Confirm Date/Time Change
                </Button>
            </div>
        </>
    );

    if (['lg', 'xl', '2xl'].indexOf(breakpoint) >= 0) {
        return (
            <Modal open={isOpen} onClose={() => setOpen(false)} size="lg" hideX unstyled>
                <div className="pt-6">
                    <h3 className="px-4 text-lg font-semibold text-gray-900">Select a new date & time</h3>
                    {content}
                </div>
            </Modal>
        );
    }

    return (
        <Panel
            open={isOpen}
            setOpen={setOpen}
            attachedTo="bottom"
            title={<h3 className="px-3 pt-1.5 text-lg font-semibold text-gray-900">Select a new date & time</h3>}
        >
            {content}
        </Panel>
    );
}
